/**
 * Sales Room (Kundenseite des Retool-„Sales Deal Manager“): Preiskatalog, Übersetzungen und Gruppierung der
 * Angebotspositionen – übernommen aus der Retool-App (lib/pricing.ts, lib/pdf/catalogI18n.ts, lib/pdf/offerPdf.tsx),
 * damit Connect Angebote genauso darstellt wie das Angebots-PDF aus Retool. Retool bleibt führend für Katalog und
 * Angebote; Connect liest nur. Analyse: docs/sales-room/RETOOL-ANALYSE.md
 */

export type SalesLang = "de" | "en";

export interface SalesPriceItem {
  key: string;
  label: string;
  labelEn?: string;
  unit?: string;
  unitEn?: string;
  prefix?: string;
  list: number;
  price: number;
}

export interface SalesPriceSection {
  key: string;
  title: string;
  titleEn?: string;
  description?: string;
  descriptionEn?: string;
  items: SalesPriceItem[];
  hiddenForCustomer?: boolean;
}

/** Angebotsposition wie in Retool (offers.line_items) */
export interface SalesOfferLineItem {
  key?: string;
  section?: string;
  label: string;
  quantity: number;
  unitPrice: number;
}

export interface SalesOfferItem {
  label: string;
  unit?: string;
  prefix?: string;
  price: number;
}
export interface SalesOfferGroup {
  title: string;
  description?: string;
  items: SalesOfferItem[];
}

type Raw = { key: string; title: string; description?: string; items: { key: string; label: string; unit?: string; prefix?: string; list: number }[] };
const RAW_SECTIONS: Raw[] = [
  { key: "pick_pack", title: "Pick & Pack – B2C", items: [
    { key: "pp_pick", label: "Pick", unit: "je Artikel", list: 0.15 },
    { key: "pp_pack_flat", label: "Packpauschale", unit: "je Paket, 1 kg Versandgewicht", list: 0.8 },
    { key: "pp_pack_surcharge", label: "Zuschlag Packpauschale", unit: "je weiteres kg Versandgewicht", list: 0.15 },
  ] },
  { key: "packaging", title: "Packaging", description: "Standardkarton, Nassklebeband, Füllpapier", items: [
    { key: "pkg_1", label: "Bis 1 kg Versandgewicht", list: 0.24 },
    { key: "pkg_3", label: "Bis 3 kg Versandgewicht", list: 0.41 },
    { key: "pkg_5", label: "Bis 5 kg Versandgewicht", list: 0.72 },
    { key: "pkg_10", label: "Bis 10 kg Versandgewicht", list: 0.89 },
    { key: "pkg_20", label: "Bis 20 kg Versandgewicht", list: 1.36 },
    { key: "pkg_31", label: "Bis 31,5 kg Versandgewicht", list: 1.82 },
  ] },
  { key: "warehousing", title: "Warehousing", description: "Basiert auf den Produktmaßen (genaue Abrechnung)", items: [{ key: "wh_cbm", label: "Je Kubikmeter pro Tag (m³)", unit: "pro Tag", list: 0.39 }] },
  { key: "goods_receipt", title: "Warenannahme", items: [
    { key: "gr_parcel", label: "Je Paket (sortenrein, eine SKU, gekennzeichnet)", list: 1.66 },
    { key: "gr_pallet", label: "Je Palette (sortenrein, eine SKU, gekennzeichnet)", list: 4.99 },
    { key: "gr_mixed", label: "Gemischtes Paket – Abrechnung je Artikel", list: 0.1 },
  ] },
  { key: "returns", title: "Retourenhandling", items: [
    { key: "ret_flat", label: "Retourenpauschale", unit: "je Auftrag", list: 0.99 },
    { key: "ret_article", label: "Artikelhandling", unit: "je Artikel (4-Seiten-/Geruchs-Check)", list: 0.49 },
  ] },
  // Versandkonditionen: für Kunden ausgeblendet (gesonderte Preislisten), müssen aber im Katalog stehen, damit solche
  // Angebotspositionen ihrem Abschnitt zugeordnet und nicht unter „Weitere Positionen“ gezeigt werden
  { key: "shipping_de", title: "Versandkonditionen Deutschland (DHL, Auszug)", items: [
    { key: "de_small", label: "DHL Kleinpaket (bis 1 kg, max. 35 × 25 × 8 cm)", list: 3.29 },
    { key: "de_1", label: "Paket bis 1 kg", list: 3.69 },
    { key: "de_2", label: "Paket bis 2 kg", list: 4.04 },
    { key: "de_3", label: "Paket bis 3 kg", list: 4.44 },
    { key: "de_5", label: "Paket bis 5 kg", list: 5.16 },
    { key: "de_7", label: "Paket bis 7 kg", list: 6.06 },
    { key: "de_9", label: "Paket bis 9 kg", list: 7.24 },
    { key: "de_12", label: "Paket bis 12 kg", list: 8.07 },
    { key: "de_20", label: "Paket bis 20 kg", list: 39.37 },
  ] },
  { key: "shipping_intl", title: "Versandkonditionen International (Auszug)", description: "Richtpreise – „ab“ kennzeichnet gewichts-/zonenabhängige Startpreise", items: [
    { key: "intl_at", label: "Österreich", list: 6.41 },
    { key: "intl_ch", label: "Schweiz", list: 12.95, prefix: "ab" },
    { key: "intl_be", label: "Belgien", list: 11.48 },
    { key: "intl_dk", label: "Dänemark", list: 8.8 },
    { key: "intl_fr", label: "Frankreich", list: 9.33 },
    { key: "intl_it", label: "Italien", list: 7.59 },
    { key: "intl_lu", label: "Luxemburg", list: 7.87 },
    { key: "intl_nl", label: "Niederlande", list: 7.38 },
    { key: "intl_cz", label: "Tschechien", list: 6.46 },
    { key: "intl_bg", label: "Bulgarien", list: 9.95, prefix: "ab" },
    { key: "intl_ee", label: "Estland", list: 6.51, prefix: "ab" },
    { key: "intl_es", label: "Spanien", list: 5.74, prefix: "ab" },
    { key: "intl_gr", label: "Griechenland", list: 8.55, prefix: "ab" },
    { key: "intl_hr", label: "Kroatien", list: 5.13, prefix: "ab" },
    { key: "intl_hu", label: "Ungarn", list: 7.91, prefix: "ab" },
    { key: "intl_ie", label: "Irland", list: 9.95, prefix: "ab" },
    { key: "intl_lt", label: "Litauen", list: 3.86, prefix: "ab" },
    { key: "intl_pl", label: "Polen", list: 6.93, prefix: "ab" },
    { key: "intl_pt", label: "Portugal", list: 4.46, prefix: "ab" },
    { key: "intl_ro", label: "Rumänien", list: 4.16, prefix: "ab" },
    { key: "intl_sk", label: "Slowakei", list: 4.27, prefix: "ab" },
  ] },
];

/** Standardkatalog (wenn in Retool keiner gespeichert ist) – wie lib/pricing.ts defaultCatalog() */
export function defaultSalesCatalog(): SalesPriceSection[] {
  return RAW_SECTIONS.map((s) => ({
    key: s.key,
    title: s.title,
    ...(s.description ? { description: s.description } : {}),
    items: s.items.map((i) => ({ key: i.key, label: i.label, list: i.list, price: i.list, ...(i.unit ? { unit: i.unit } : {}), ...(i.prefix ? { prefix: i.prefix } : {}) })),
  }));
}

const SECTION_TITLE_EN: Record<string, string> = { pick_pack: "Pick & Pack – B2C", packaging: "Packaging", warehousing: "Warehousing", goods_receipt: "Goods receipt", returns: "Returns handling", shipping_de: "Shipping rates Germany (DHL, excerpt)", shipping_intl: "International shipping rates (excerpt)" };
const SECTION_DESC_EN: Record<string, string> = { packaging: "Standard carton, gummed tape, filling paper", warehousing: "Based on product dimensions (exact billing)", shipping_intl: 'Guide prices – "from" indicates weight-/zone-dependent starting prices' };
const ITEM_LABEL_EN: Record<string, string> = {
  pp_pick: "Pick", pp_pack_flat: "Packing flat rate", pp_pack_surcharge: "Packing flat-rate surcharge",
  pkg_1: "Up to 1 kg shipping weight", pkg_3: "Up to 3 kg shipping weight", pkg_5: "Up to 5 kg shipping weight", pkg_10: "Up to 10 kg shipping weight", pkg_20: "Up to 20 kg shipping weight", pkg_31: "Up to 31.5 kg shipping weight",
  wh_cbm: "Per cubic metre per day (m³)", gr_parcel: "Per parcel (single SKU, labelled)", gr_pallet: "Per pallet (single SKU, labelled)", gr_mixed: "Mixed parcel – billed per item",
  ret_flat: "Returns flat rate", ret_article: "Item handling",
};
const UNIT_EN: Record<string, string> = { "je Artikel": "per item", "je Paket, 1 kg Versandgewicht": "per parcel, 1 kg shipping weight", "je weiteres kg Versandgewicht": "per additional kg shipping weight", "pro Tag": "per day", "je Auftrag": "per order", "je Artikel (4-Seiten-/Geruchs-Check)": "per item (4-sided/odour check)" };
const PREFIX_EN: Record<string, string> = { ab: "from" };
const MORE_ITEMS: Record<SalesLang, string> = { de: "Weitere Positionen", en: "Additional items" };

const sectionTitle = (s: SalesPriceSection, l: SalesLang) => (l === "en" ? s.titleEn?.trim() || SECTION_TITLE_EN[s.key] || s.title : s.title);
const sectionDesc = (s: SalesPriceSection, l: SalesLang) => (s.description == null ? undefined : l === "en" ? s.descriptionEn?.trim() || SECTION_DESC_EN[s.key] || s.description : s.description);
const itemLabel = (i: SalesPriceItem, l: SalesLang) => (l === "en" ? i.labelEn?.trim() || ITEM_LABEL_EN[i.key] || i.label : i.label);
const itemUnit = (i: SalesPriceItem, l: SalesLang) => (i.unit == null ? undefined : l === "en" ? i.unitEn?.trim() || UNIT_EN[i.unit] || i.unit : i.unit);
const itemPrefix = (p: string | undefined, l: SalesLang) => (p == null ? undefined : l === "en" ? PREFIX_EN[p] ?? p : p);

/** Abschnitte, die Kunden nicht sehen (Versandkonditionen/Nebengebühren – kommen über gesonderte Preislisten) */
export function isCustomerHiddenSection(s: Pick<SalesPriceSection, "key" | "title" | "hiddenForCustomer">): boolean {
  if (typeof s.hiddenForCustomer === "boolean") return s.hiddenForCustomer;
  if (s.key === "shipping_de" || s.key === "shipping_intl") return true;
  const t = (s.title ?? "").toLowerCase();
  return t.includes("versandkonditionen") || t.includes("nebengebühren") || t.includes("nebengebuehren");
}

/** Katalog aus Retool (app_settings.price_catalog, JSON) – unbrauchbar → Standardkatalog */
export function parseSalesCatalog(raw: string | null | undefined): SalesPriceSection[] {
  if (!raw) return defaultSalesCatalog();
  try {
    const v = JSON.parse(raw) as unknown;
    if (Array.isArray(v) && v.length) return v as SalesPriceSection[];
  } catch {
    /* Standard */
  }
  return defaultSalesCatalog();
}

/**
 * Angebotspositionen nach Katalog gruppieren (Reihenfolge, Bezeichnung, Einheit aus dem Katalog; Preis aus dem Angebot).
 * Wie Retool buildGroupsFromLineItems: Positionen aus ausgeblendeten Abschnitten werden verbraucht, aber nicht gezeigt.
 */
export function salesOfferGroups(lineItems: SalesOfferLineItem[], sections: SalesPriceSection[], lang: SalesLang = "de"): SalesOfferGroup[] {
  const groups: SalesOfferGroup[] = [];
  const used = new Set<number>();
  for (const section of sections) {
    const items: SalesOfferItem[] = [];
    for (const it of section.items ?? []) {
      const idx = lineItems.findIndex((li, i) => !used.has(i) && li.key === it.key);
      if (idx < 0) continue;
      used.add(idx);
      const unit = itemUnit(it, lang);
      const prefix = itemPrefix(it.prefix, lang);
      items.push({ label: itemLabel(it, lang), price: Number(lineItems[idx]!.unitPrice) || 0, ...(unit ? { unit } : {}), ...(prefix ? { prefix } : {}) });
    }
    lineItems.forEach((li, i) => {
      if (used.has(i) || li.key || li.section !== section.key) return;
      used.add(i);
      items.push({ label: li.label, price: Number(li.unitPrice) || 0 });
    });
    if (items.length && !isCustomerHiddenSection(section)) {
      const description = sectionDesc(section, lang);
      groups.push({ title: sectionTitle(section, lang), items, ...(description ? { description } : {}) });
    }
  }
  const rest: SalesOfferItem[] = [];
  lineItems.forEach((li, i) => {
    if (!used.has(i)) rest.push({ label: li.label, price: Number(li.unitPrice) || 0 });
  });
  if (rest.length) groups.push({ title: MORE_ITEMS[lang], items: rest });
  return groups;
}

/** Preis einer Katalogposition (Rechner) */
export function salesCatalogPrice(sections: SalesPriceSection[], key: string, fallback: number): number {
  for (const s of sections) for (const i of s.items ?? []) if (i.key === key) return Number(i.price) || 0;
  return fallback;
}

/** Pick-&-Pack-Rechner (wie Retool): Pakete × Packpauschale + Pakete × Artikel je Paket × Pick */
export function pickPackEstimate(packages: number, itemsPerPackage: number, prices: { pick: number; pack: number }) {
  const pkg = Math.max(0, packages || 0);
  const items = Math.max(0, itemsPerPackage || 0);
  const packCost = pkg * prices.pack;
  const pickCost = pkg * items * prices.pick;
  return { packCost, pickCost, monthly: packCost + pickCost };
}

export interface SalesWelcomeText { badge: string; title: string; subtitle: string }
export interface SalesFaqItem { q: string; a: string }

export const DEFAULT_SALES_WELCOME: Record<SalesLang, SalesWelcomeText> = {
  de: { badge: "Persönliches Angebot", title: "Willkommen, {name}", subtitle: "Ihr individuelles Angebot „{titel}“ im Überblick." },
  en: { badge: "Personal offer", title: "Welcome, {name}", subtitle: "Your individual offer “{titel}” at a glance." },
};
export const DEFAULT_SALES_BRANDING = {
  companyName: "Logiful",
  footer: { de: "Alle Preise netto zzgl. gesetzlicher USt. Die Abrechnung erfolgt verbrauchsabhängig nach den genannten Konditionen.", en: "All prices are net, plus statutory VAT. Billing is based on actual usage according to the stated conditions." },
  intro: { de: "gerne unterbreiten wir Ihnen folgendes Angebot für die Fulfillment-Leistungen von Logiful.", en: "we are pleased to submit the following offer for the fulfilment services of Logiful." },
  highlights: { de: ["Persönlicher Ansprechpartner", "Keine Einrichtungsgebühr", "Transparente, verbrauchsabhängige Abrechnung"], en: ["Personal point of contact", "No setup fee", "Transparent, usage-based billing"] },
} as const;

/** Sichtbare Blöcke je Deal (deals.sales_room_blocks) – fehlend = sichtbar; Präsentation liegt in Retool Storage (noch nicht in Connect) */
export const SALES_ROOM_BLOCKS = ["welcome", "presentation", "offer", "calculator", "becomeCustomer", "faq"] as const;
export type SalesRoomBlock = (typeof SALES_ROOM_BLOCKS)[number];
export function salesRoomBlocks(v: unknown): Record<SalesRoomBlock, boolean> {
  const o = (v && typeof v === "object" ? v : {}) as Record<string, unknown>;
  return Object.fromEntries(SALES_ROOM_BLOCKS.map((k) => [k, o[k] !== false])) as Record<SalesRoomBlock, boolean>;
}

/** Platzhalter im Begrüßungstext ({name}, {firma}, {titel}) */
export const fillSalesPlaceholders = (text: string, d: { contactName: string | null; company: string | null; title: string | null }) =>
  text.replace(/\{name\}/g, d.contactName ?? d.company ?? "").replace(/\{firma\}/g, d.company ?? "").replace(/\{titel\}/g, d.title ?? "");
